import { Typography, useTheme } from '@material-ui/core';
import styledConsts from 'app/styles/styledConsts';
import styledMixins from 'app/styles/styledMixins';
import { ThemeTextColor, themeTextColors } from 'app/styles/theme';
import { StringOr } from 'app/tools/general.types';
import clsx from 'clsx';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { RequireExactlyOne } from 'type-fest';

import IconLib from './icon-lib';

const FillImage = styled.img`
  ${styledMixins.fullsize}
`;

type TypographyOpts = {
  $size: number;
  $color?: string;
  $pale?: boolean;
};
const containerStyle = css<TypographyOpts>`
  &,
  & > * {
    width: ${p => p.$size}px;
    height: ${p => p.$size}px;
  }
  margin: auto;
  opacity: ${p => (p.$pale ? 0.2 : 1)};

  ${p =>
    p.$color &&
    css`
      color: ${p.$color};
    `};
`;
const AssetContainer = styled.div`
  ${containerStyle}
`;

const InlineIcon = styled.span<TypographyOpts>`
  font-size: ${p => p.$size}em;
  ${p =>
    p.$color &&
    css`
      color: ${p.$color};
    `};
`;
const IconContainer = styled(Typography)`
  ${containerStyle}
  font-size: ${p => p.$size}px;
`;

type Props = RequireExactlyOne<{
  icon: IconLib;
  asset: string;
  imgNode: React.ReactNode;
}> & {
  size?: number;
  emSize?: number;
  color?: StringOr<ThemeTextColor>;
  soloContentStyle?: boolean;
  inline?: boolean;
  alt?: string;
  className?: string;
  onClick?: () => void;
};

export default function Icon({
  icon,
  asset,
  imgNode,
  color,
  soloContentStyle,
  inline,
  emSize = 1,
  size,
  alt = 'icon',
  onClick,
  className,
}: Props): JSX.Element {
  const theme = useTheme();

  const colorProp =
    color && themeTextColors.find(c => c === color)
      ? { color: color as ThemeTextColor }
      : color === 'warning'
      ? { $color: styledConsts.cWarning({ theme }) }
      : soloContentStyle
      ? { color: 'primary' as const }
      : { $color: color };

  const defaultIconSize = (soloContentStyle && 120) || 24;

  return !icon ? (
    <div {...{ className }}>
      <AssetContainer $size={size || defaultIconSize}>
        {imgNode || <FillImage src={asset} {...{ alt }} />}
      </AssetContainer>
    </div>
  ) : inline ? (
    <InlineIcon
      $size={emSize}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={clsx(`icon-${icon}`, className)}
      {...colorProp}
    />
  ) : (
    <div {...{ className }}>
      <IconContainer
        $size={size || defaultIconSize}
        $pale={soloContentStyle}
        className={`icon-${icon}`}
        {...{ onClick }}
        {...colorProp}
      />
    </div>
  );
}
